import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {BulkAssemblyNewWorklistInfo} from 'bulk-workflows/assembly/BulkAssemblyConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import FolderSelector from 'file-browser/FolderSelector';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {capitalize} from 'util/NameUtil';
import {GenericEvent, InputChangeEvent} from 'util/Types';
import AddToWorklistForm, {WorklistFormSelectorType} from 'worklists/AddToWorklistForm';
import {WorklistFormType, WorklistSummary, WorklistType} from 'worklists/WorklistConstants';

export const DEFAULT_SAVE_INFORMATION = {
  worklistParams: null,
};
export const DEFAULT_WORKLIST_PARAMS = {
  worklistFormType: WorklistFormType.NEW,
  targetWorklistId: null,
  newWorklistInfo: null,
};

export interface WorklistInfo {
  worklistFormType: WorklistFormType;
  targetWorklistId: string | null;
  newWorklistInfo: BulkAssemblyNewWorklistInfo | null;
}

export interface LocationInformation {
  targetFolderId?: string;
  worklistParams: WorklistInfo | null;
  tagSchemaId: string | null;
}

interface TagSchemaInfo {
  tagSchemaId: string | null;
  onUpdateTagSchemaId: (tagSchemaId: string | null) => void;
  oligoAwareFolderItemTypes: Array<OligoAwareFolderItemType> | null;
}

export interface WorklistParams {
  newWorklistOwnerId: string;
  useWorklistCheckboxLabel: string;
  pluralEntityLabel: string;
  shouldShowOverflowWarningForNewWorklist: boolean;
  shouldShowOverflowWarningForExistingWorklist: boolean;
}

export interface SaveLocationPickerProps {
  uniqueId: string;
  targetFolderId?: string;
  onTargetFolderIdChange: (e: GenericEvent) => void;
  tagSchemaInfo: TagSchemaInfo | null;
  worklistParams: WorklistParams;
  useWorklist: boolean;
  onUseWorklistChange: (useWorklist: boolean) => void;
  worklistFormType: WorklistFormType;
  onWorklistFormTypeChange: (newOrExisting: WorklistFormType) => void;
  newWorklistName: string;
  onNewWorklistNameChange: (e: InputChangeEvent) => void;
  onWorklistIdChange: (worklistSummary: WorklistSummary | null) => void;
  required?: boolean;
}

/**
 * A controlled component to pick locations and optional worklists to save assembly outputs.
 *
 * @param targetFolderId The folder to save the assembly constructs in.
 * @param onTargetFolderIdChange A function to be called when targetFolderId changes.
 * @param tagSchemaInfo (optional) Information about the selected tag schema, or null if updating schema
 *                          is not supported.
 * @param worklistParams Paramters for the worklist to save to.
 * @param uniqueId A key to differentiate this SaveLocationPicker from all other SaveLocationPicker in the DOM
 *                 at any given point in time. Checkbox labels uses htmlFor to associate themselves with a
 *                 specific checkbox input, so the id needs to be unique.
 * @param useWorklist Whether or not to add assembly outputs to a worklist.
 * @param onUseWorklistChange A function to be called when useWorklist changes.
 * @param worklistFormType Whether to create a new worklist for assembly outputs or to use an existing one.
 * @param onWorklistFormTypeChange A function to be called when worklistFormType changes.
 * @param newWorklistName What to name the new worklist that assembly outputs will be added to.
 * @param onNewWorklistNameChange A function to be called when newWorklistName changes.
 * @param onWorklistIdChange A function to be called when an existing worklist is selected.
 * @param required Whether or not this assembly output actually has to be saved.
 * @constructor
 */
export default function SaveLocationPicker({
  targetFolderId,
  onTargetFolderIdChange,
  tagSchemaInfo,
  worklistParams,
  uniqueId,
  useWorklist,
  onUseWorklistChange,
  worklistFormType,
  onWorklistFormTypeChange,
  newWorklistName,
  onNewWorklistNameChange,
  onWorklistIdChange,
  required = true,
}: SaveLocationPickerProps): JSX.Element {
  const shouldShowWarningBanner =
    (worklistFormType === WorklistFormType.NEW && worklistParams.shouldShowOverflowWarningForNewWorklist) ||
    (worklistFormType === WorklistFormType.EXISTING &&
      worklistParams.shouldShowOverflowWarningForExistingWorklist);
  return (
    <div>
      <div className={css(styles.folderAndSchemaWrapper)}>
        <div className={css(styles.locationSelectorWrapper)}>
          <FieldLabel label="Set location" isRequired={required} />
          <FolderSelector
            value={targetFolderId}
            onChange={onTargetFolderIdChange}
            showInventory={true}
            allowNone={!required}
            enableRedesign={true}
            unsetOnFetchFail={true}
          />
        </div>
        {tagSchemaInfo != null && (
          <div className={css(styles.locationSelectorWrapper)}>
            <FieldLabel label="Set schema" isRequired={false} />
            <TagSchemaSelector
              selectedTagSchemaId={tagSchemaInfo.tagSchemaId}
              onChange={(tagSchema) => {
                tagSchemaInfo.onUpdateTagSchemaId(tagSchema != null ? tagSchema.id : null);
              }}
              oligoAwareFolderItemTypes={tagSchemaInfo.oligoAwareFolderItemTypes}
            />
          </div>
        )}
      </div>
      {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && (
        <>
          <CheckboxInput
            checked={useWorklist}
            id={`save-location-picker-use-worklist-checkbox-${uniqueId}`}
            label={worklistParams.useWorklistCheckboxLabel}
            onChange={(e) => {
              onUseWorklistChange(e.target.checked);
            }}
            className={css(styles.checkbox)}
          />
          {useWorklist && (
            <div className={css(styles.worklistSection)}>
              <FieldLabel label="Worklist" isRequired={false} />
              {shouldShowWarningBanner && (
                <InlineBanner
                  bannerType={InlineBannerType.WARNING}
                  text={`This assembly contains a large number of constructs. Worklists are limited to ${BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST} entities, so it is possible that some of your new entities will not fit in a single worklist. Any overflowing entities will be added to additional worklists with the same name and a number; for example, "${capitalize(worklistParams.pluralEntityLabel)} (1)", "${capitalize(worklistParams.pluralEntityLabel)} (2)," and so on.`}
                />
              )}
              <br />
              <AddToWorklistForm
                autoFocus={false}
                useFullWidthToggle={true}
                ids={[]}
                refreshOptions={{refreshRecentWorklists: true}}
                worklistType={WorklistType.BIOENTITY}
                newOrExisting={worklistFormType}
                setNewOrExisting={onWorklistFormTypeChange}
                newWorklistName={{value: newWorklistName, onChange: onNewWorklistNameChange}}
                newWorklistOwnerId={worklistParams.newWorklistOwnerId}
                updateWorklistId={onWorklistIdChange}
                required={true}
                selectorType={WorklistFormSelectorType.TOGGLE_GROUP}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  checkbox: {marginTop: 16},
  folderAndSchemaWrapper: {display: 'flex', columnGap: '10px'},
  locationSelectorWrapper: {flex: '50%'},
  worklistSection: {marginTop: 16},
});
